import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, X, HelpCircle } from 'lucide-react';
import { useState, useMemo } from 'react';
import { usePersonaProfiles } from '@/hooks/usePersonaCatalog';
import type { UserProfileId } from '@/types/userProfileTypes';
import type { CatalogData, Tradeoffs, UserType, AdvancedControls, ScenarioPreset } from '@/types';
import { PRECISION_OPTIONS } from '@/lib/sizingOptions';
import { ModelSizeSlider } from '@/components/sizing/ModelSizeSlider';
interface QuickControlsCollapsibleProps {
  tradeoffs: Tradeoffs;
  users: UserType[];
  totalPct: number;
  warningUnderPct: boolean;
  blockedOverPct: boolean;
  catalog?: CatalogData | null;
  catalogLoading?: boolean;
  selectedModelId: string | null;
  onModelChange: (modelId: string) => void;
  onTradeoffChange: <K extends keyof Tradeoffs>(key: K, value: Tradeoffs[K]) => void;
  onUserUpdate: (id: string, patch: Partial<UserType>) => void;
  onUserRemove: (id: string) => void;
  onUserAdd: () => void;
  advanced: AdvancedControls;
  onAdvancedChange: <K extends keyof AdvancedControls>(key: K, value: AdvancedControls[K]) => void;
  sticky?: boolean;
  presets?: ScenarioPreset[];
  onPresetSelect?: (presetId: string) => void;
}
export const QuickControlsCollapsible = ({
  tradeoffs,
  users,
  totalPct,
  warningUnderPct,
  blockedOverPct,
  catalog,
  catalogLoading = false,
  selectedModelId,
  onModelChange,
  onTradeoffChange,
  onUserUpdate,
  onUserRemove,
  onUserAdd,
  advanced,
  onAdvancedChange,
  sticky = false,
  presets = [],
  onPresetSelect
}: QuickControlsCollapsibleProps) => {
  const precisionIndex = Math.max(0, PRECISION_OPTIONS.findIndex(option => option.value === tradeoffs.precision));
  const activePrecision = PRECISION_OPTIONS[precisionIndex] ?? PRECISION_OPTIONS[1];
  const {
    data: rawPersonas
  } = usePersonaProfiles();
  const personas = useMemo(() => {
    if (!rawPersonas) return undefined;
    return rawPersonas.map(p => ({
      ...p,
      display_name: (p as any).display_name || (p as any).name || p.id
    }));
  }, [rawPersonas]);
  const profileOptions = useMemo(() => {
    if (!personas) return [];
    return personas.map(profile => ({
      id: profile.id,
      label: profile.display_name,
      inputTokens: profile.prompt_tokens_per_turn,
      outputTokens: profile.response_tokens_per_turn
    }));
  }, [personas]);
  const handleProfileChange = (userUid: string, value: string) => {
    if (value === 'custom') {
      onUserUpdate(userUid, {
        profileId: 'custom'
      });
      return;
    }
    const profile = personas?.find(p => p.id === value);
    if (!profile) return;
    onUserUpdate(userUid, {
      profileId: value as UserProfileId | 'custom',
      label: profile.display_name,
      inTok: profile.prompt_tokens_per_turn,
      outTok: profile.response_tokens_per_turn
    });
  };
  const LATENCY_TARGETS = [{
    index: 0,
    targetMs: 500,
    latencyMs: 500,
    ttftFloorMs: 150,
    label: 'Aggressive',
    sublabel: '500ms',
    policy: 'streaming' as const,
    tolerance: 'aggressive' as const
  }, {
    index: 1,
    targetMs: 1500,
    latencyMs: 1500,
    ttftFloorMs: 300,
    label: 'Fast',
    sublabel: '1.5s',
    policy: 'streaming' as const,
    tolerance: 'fast' as const
  }, {
    index: 2,
    targetMs: 3000,
    latencyMs: 3000,
    ttftFloorMs: 450,
    label: 'Balanced',
    sublabel: '3s',
    policy: 'streaming' as const,
    tolerance: 'balanced' as const
  }, {
    index: 3,
    targetMs: 6000,
    latencyMs: 6000,
    ttftFloorMs: 800,
    label: 'Relaxed',
    sublabel: '6s',
    policy: 'nonstreaming' as const,
    tolerance: 'relaxed' as const
  }, {
    index: 4,
    targetMs: undefined,
    latencyMs: 0,
    ttftFloorMs: 0,
    label: 'No constraint',
    sublabel: '',
    policy: 'streaming' as const,
    tolerance: 'none' as const
  }] as const;
  const resolveCurrentLatencyTarget = () => {
    const toleranceKey =
      tradeoffs.latencyTolerance === 'queue' ? 'none' : tradeoffs.latencyTolerance;
    const toleranceConfig = LATENCY_TARGETS.find(t => t.tolerance === toleranceKey);
    if (toleranceConfig) return toleranceConfig.targetMs;
    if (typeof advanced.latencyTargetMs === 'number' && advanced.latencyTargetMs > 0) {
      return advanced.latencyTargetMs;
    }
    if (typeof advanced.latencyMs === 'number' && advanced.latencyMs > 0) {
      return advanced.latencyMs;
    }
    return undefined;
  };
  const latencyTargetToIndex = (ms: number | undefined): number => {
    if (ms === undefined) return LATENCY_TARGETS[LATENCY_TARGETS.length - 1]!.index;
    const closest = LATENCY_TARGETS.slice(0, -1).reduce((prev, curr) => Math.abs((curr.targetMs ?? 0) - ms) < Math.abs((prev.targetMs ?? 0) - ms) ? curr : prev);
    return closest.index;
  };
  const indexToLatencyConfig = (index: number) => LATENCY_TARGETS[index];
  const getLatencyLabel = (ms: number | undefined): string => {
    if (ms === undefined) return 'No constraint';
    const target = LATENCY_TARGETS.find(t => t.targetMs === ms);
    return target ? target.label : `${ms}ms`;
  };
  const currentLatencyTarget = resolveCurrentLatencyTarget();
  return <TooltipProvider>
      <div className={`space-y-2 rounded-xl border border-border/50 bg-primary/5 p-4 backdrop-blur ${sticky ? 'sticky top-6' : ''}`}>
        {/* Header */}
        <div className="border-b border-border/50 pb-3">
          <h3 className="font-semibold text-foreground text-xl">Configure Your Scenario    </h3>
          <p className="text-xs text-muted-foreground">Adjust workload parameters</p>
        </div>

        {/* Quick Start Scenarios */}
        {presets.length > 0 && onPresetSelect && <>
            <div className="space-y-2">
              <div className="space-y-1">
                <Label className="text-xs font-medium">Quick Start</Label>
                <p className="text-[10px] text-muted-foreground leading-relaxed">
                  Choose a preset scenario to get started quickly
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {presets.map(preset => <Button key={preset.id} variant="outline" size="sm" onClick={() => onPresetSelect(preset.id)} className="h-auto flex-col items-start gap-1 p-2 text-left">
                    <span className="text-xs font-medium">{preset.label}</span>
                    <span className="text-[10px] text-muted-foreground leading-tight">
                      {preset.description}
                    </span>
                  </Button>)}
              </div>
            </div>

            {/* Separator */}
            <div className="flex items-center gap-2 py-1">
              <div className="h-px flex-1 bg-border/50" />
              <span className="text-[10px] text-muted-foreground">or customize</span>
              <div className="h-px flex-1 bg-border/50" />
            </div>
          </>}

        {/* User Types Section */}
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-xs font-medium">User Types</Label>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Define your user personas and their token usage patterns. Adjust the percentage mix to match your expected user distribution.
            </p>
          </div>
          <div className="space-y-2">
            {users.map(user => {
            const userKey = user.uid ?? user.id;
            const selectValue = user.profileId ?? 'custom';
            return <div key={userKey} className="flex items-center gap-2 rounded-lg border border-border/50 bg-background/50 p-2">
                      <div className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{
                backgroundColor: user.color
              }} />
                      <Select value={selectValue} onValueChange={value => handleProfileChange(userKey, value)}>
                        <SelectTrigger className="h-7 min-w-0 flex-1 border-0 bg-transparent text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {profileOptions.map(option => <SelectItem key={option.id} value={option.id}>
                              <div className="flex items-center justify-between w-full gap-3">
                                <span className="text-xs">{option.label}</span>
                                <span className="text-[10px] text-muted-foreground font-mono">
                                  {option.inputTokens} input, {option.outputTokens} output tokens
                                </span>
                              </div>
                            </SelectItem>)}
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="number" value={user.pct ?? 0} onChange={e => onUserUpdate(userKey, {
                pct: parseFloat(e.target.value) || 0
              })} className="h-7 w-16 text-xs" min="0" max="100" />
                      <span className="text-xs text-muted-foreground">%</span>
                      {users.length > 1 && <Button variant="ghost" size="sm" onClick={() => onUserRemove(userKey)} className="h-7 w-7 flex-shrink-0 p-0">
                          <X className="h-3 w-3" />
                        </Button>}
                    </div>;
          })}

                <Button variant="outline" size="sm" onClick={onUserAdd} className="h-7 w-full text-xs" disabled={users.length >= 4}>
                  <Plus className="mr-1 h-3 w-3" />
                  Add Type
                </Button>

                <div className={`flex items-center justify-between rounded px-2 py-1 text-xs ${blockedOverPct ? 'bg-destructive/10 text-destructive' : warningUnderPct ? 'bg-amber-500/10 text-amber-600' : 'bg-green-500/10 text-green-600'}`}>
                  <span>Total:</span>
                  <span className="font-semibold">{totalPct.toFixed(0)}%</span>
                </div>
              </div>
            </div>

          {/* Concurrent Users */}
          <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs font-medium">Concurrent Users</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-3 w-3 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-xs">Number of users actively using the system at the same time</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <span className="text-xs font-semibold text-foreground">{tradeoffs.concurrentUsers.toLocaleString()}</span>
              </div>
              <Slider value={[tradeoffs.concurrentUsers]} onValueChange={([value]) => onTradeoffChange('concurrentUsers', value)} min={10} max={20000} step={10} className="py-1" />
            </div>

        {/* Chat Memory */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Label className="text-xs font-medium">Chat Memory</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3 w-3 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">Number of conversation turns kept in memory (KV cache)</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <span className="text-xs font-semibold text-foreground">{tradeoffs.turns} turns</span>
          </div>
          <Slider value={[tradeoffs.turns]} onValueChange={([value]) => onTradeoffChange('turns', value)} min={1} max={50} step={1} className="py-1" />
        </div>

        {/* Response Speed */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Label className="text-xs font-medium">Response Speed</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3 w-3 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">Target latency for user responses</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <span className="text-xs font-semibold text-foreground">{getLatencyLabel(currentLatencyTarget)}</span>
          </div>
          <Slider value={[latencyTargetToIndex(currentLatencyTarget)]} onValueChange={([value]) => {
          const targetConfig = indexToLatencyConfig(value ?? LATENCY_TARGETS.length - 1);
          const target = targetConfig?.targetMs;
          onTradeoffChange('latencyTolerance', targetConfig?.tolerance ?? tradeoffs.latencyTolerance);
          onAdvancedChange('latencyTargetMs', target);
          if (targetConfig) {
            if (typeof targetConfig.latencyMs === 'number') {
              onAdvancedChange('latencyMs', targetConfig.latencyMs);
            }
            onAdvancedChange('latencyPolicy', targetConfig.policy);
          }
        }} min={0} max={4} step={1} className="py-1" />
        </div>

        {/* Model Selection - No label */}
        {catalogLoading ? <div className="h-16 animate-pulse rounded bg-muted" /> : <ModelSizeSlider catalog={catalog} selectedModelId={selectedModelId} onChange={onModelChange} />}

        {/* Precision */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Label className="text-xs font-medium">Precision</Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3 w-3 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">Number format precision (lower = faster, less memory)</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <span className="text-xs font-semibold text-foreground">{activePrecision.label}</span>
          </div>
          <Slider value={[precisionIndex]} onValueChange={([value]) => {
          const raw = Number.isFinite(value) ? value! : precisionIndex;
          const clamped = Math.max(0, Math.min(PRECISION_OPTIONS.length - 1, Math.round(raw)));
          const opt = PRECISION_OPTIONS[clamped];
          if (opt) onTradeoffChange('precision', opt.value);
        }} min={0} max={PRECISION_OPTIONS.length - 1} step={1} className="py-1" />
        </div>
      </div>
    </TooltipProvider>;
};
